// Helicopter render (M36.3, SAR-5): a simple box-rig — cabin + tail boom + a spinning
// main rotor + tail rotor — posed from the sim helicopter's (pos, yaw, tilt). Client-
// only (ENG-1); the sim owns the flight model. A proper model is deferred (as the
// aeroplane's box-rig is).
import * as THREE from 'three';
import type { Helicopter } from '../../sim/aviation/helicopter';

const BODY = 0x2b6a3a; // olive SAR green
const DARK = 0x1a1a1a;

export class HelicopterRenderer {
  private readonly group = new THREE.Group();
  private readonly rigs = new Map<number, { rig: THREE.Group; rotor: THREE.Mesh; tail: THREE.Mesh }>();

  constructor(scene: THREE.Scene) { scene.add(this.group); }

  private buildRig(): { rig: THREE.Group; rotor: THREE.Mesh; tail: THREE.Mesh } {
    const rig = new THREE.Group();
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.2, 2.2), new THREE.MeshLambertMaterial({ color: BODY }));
    cabin.position.y = 0.2;
    const boom = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.3, 2.2), new THREE.MeshLambertMaterial({ color: BODY }));
    boom.position.set(0, 0.5, 1.9);
    const rotor = new THREE.Mesh(new THREE.BoxGeometry(6, 0.06, 0.24), new THREE.MeshLambertMaterial({ color: DARK }));
    rotor.position.y = 1.1;
    const tail = new THREE.Mesh(new THREE.BoxGeometry(0.06, 1.4, 0.2), new THREE.MeshLambertMaterial({ color: DARK }));
    tail.position.set(0.2, 0.6, 3.0);
    const skids = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.1, 2.0), new THREE.MeshLambertMaterial({ color: DARK }));
    skids.position.y = -0.6;
    rig.add(cabin, boom, rotor, tail, skids);
    return { rig, rotor, tail };
  }

  update(helis: readonly Helicopter[]): void {
    const seen = new Set<number>();
    for (const hc of helis) {
      seen.add(hc.id);
      let e = this.rigs.get(hc.id);
      if (!e) { e = this.buildRig(); this.rigs.set(hc.id, e); this.group.add(e.rig); }
      e.rig.position.set(hc.x, hc.y, hc.z);
      e.rig.rotation.set(hc.pitchTilt, hc.yaw, -hc.rollTilt, 'YXZ');
      e.rotor.rotation.y += 0.9; // spin the main rotor
      e.tail.rotation.x += 1.2;  // and the tail rotor
    }
    for (const [id, e] of this.rigs) {
      if (seen.has(id)) continue;
      this.group.remove(e.rig);
      e.rig.traverse((o) => { if (o instanceof THREE.Mesh) o.material.dispose(); });
      this.rigs.delete(id);
    }
  }

  clear(): void {
    for (const e of this.rigs.values()) {
      this.group.remove(e.rig);
      e.rig.traverse((o) => { if (o instanceof THREE.Mesh) o.material.dispose(); });
    }
    this.rigs.clear();
  }
}
